"use client";

import { useState } from "react";
import Link from "next/link";
import Typography from "@/components/common/typography";
import { Heading } from "@/components/common/typography/Heading";
import ClientCarousel, {
  CarouselItem,
} from "@/components/common/ui/ClientCarousel";

export type FeaturedProvince = {
  province: string;
  slug: string;
};

export type CarouselLocation = {
  city: string;
  slug: string;
  region?: string | null;
};

type FeaturedCitiesExplorerProps = {
  usa: FeaturedProvince[];
  ca: FeaturedProvince[];
  locations: {
    usa: CarouselLocation[];
    ca: CarouselLocation[];
  };
};

type Country = "usa" | "ca";

const CITIES_PER_CARD = 5;

function chunk<T>(items: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
}

// Groups locations by their Directus region, keeping first-appearance order.
// Locations with no region set end up in trailing title-less cards, chunked
// so a large unassigned set doesn't become one long list.
function groupByRegion(locations: CarouselLocation[]) {
  const byRegion = new Map<string, CarouselLocation[]>();
  const unassigned: CarouselLocation[] = [];

  locations.forEach((location) => {
    const region = location.region?.trim();
    if (region) {
      byRegion.set(region, [...(byRegion.get(region) ?? []), location]);
    } else {
      unassigned.push(location);
    }
  });

  const groups = Array.from(byRegion, ([region, items]) => ({
    region,
    items,
    hideTitle: false,
  }));

  // Non-breaking space keeps the invisible heading the same height as the
  // titled cards so the location lists line up.
  chunk(unassigned, CITIES_PER_CARD).forEach((items) => {
    groups.push({ region: "\u00A0", items, hideTitle: true });
  });

  return groups;
}

// Directus slugs for CA can come through without a leading slash (e.g.
// "ca/state/ontario"); anchor them so they don't resolve relative to the page.
function normalizeHref(href: string) {
  return href.startsWith("/") ? href : `/${href}`;
}

const pillClass = (active: boolean, className?: string) =>
  `transition-all flex items-center justify-center duration-300 ease-in-out h-10 py-1.5 rounded-full text-center cursor-pointer ${
    active ? "bg-black text-white" : "bg-bgColor text-black"
  } ${className ?? ""}`;

function RegionCard({
  region,
  locations,
  hideTitle,
}: {
  region: string;
  locations: CarouselLocation[];
  hideTitle: boolean;
}) {
  return (
    <article className="flex flex-col gap-3 min-w-[150px] lg:min-w-[19.025rem] h-full">
      <Heading
        as="h3"
        size="unresponsiveH6"
        weight={500}
        aria-hidden={hideTitle || undefined}
        className={hideTitle ? "invisible" : "underline"}
      >
        {region}
      </Heading>
      <ul className="flex flex-col gap-2">
        {locations.map((location, index) => (
          <li key={`${location.slug}-${index}`} className="w-fit">
            <Link href={normalizeHref(location.slug)} className="w-fit">
              <Typography as="span" size="unresponsiveBody1" className="w-fit">
                Car Detailing in {location.city}
              </Typography>
            </Link>
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function FeaturedCitiesExplorer({
  usa,
  ca,
  locations,
}: FeaturedCitiesExplorerProps) {
  const [country, setCountry] = useState<Country>("usa");

  const provinces = country === "usa" ? usa : ca;
  const regions = groupByRegion(locations[country]);

  return (
    <>
      <div className="flex w-fit items-center gap-4 contentContainer">
        <button
          type="button"
          onClick={() => setCountry("usa")}
          className={pillClass(country === "usa", "w-36")}
        >
          USA
        </button>
        <button
          type="button"
          onClick={() => setCountry("ca")}
          className={pillClass(country === "ca", "w-36")}
        >
          Canada
        </button>
      </div>

      <div className="mt-5 contentContainer">
        <hr className="border-t border-lightGray mb-5" />
        <h3 className="justify-start text-neutral-500 text-sm font-medium uppercase leading-5 tracking-wide">
          Browse by {country === "usa" ? "state" : "province"}
        </h3>
        <div className="flex flex-wrap items-center gap-3 mt-3">
          {provinces.map((state) => (
            <Link
              key={state.slug}
              href={normalizeHref(state.slug)}
              className="px-4 py-2 bg-white rounded-[999px] outline outline-offset-[-1px] outline-gray-200 inline-flex justify-start items-center overflow-hidden text-neutral-800 text-base font-normal leading-6"
            >
              {state.province}
            </Link>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <ClientCarousel cursorGrab={false} buttons={false}>
          {regions.map(({ region, items, hideTitle }, idx) => (
            <CarouselItem
              firstCard={idx === 0}
              className={idx === 0 ? "pl-0" : "pl-8 lg:pl-6"}
              edgeCase
              key={`${country}-region-${idx}`}
            >
              <RegionCard
                region={region}
                locations={items}
                hideTitle={hideTitle}
              />
            </CarouselItem>
          ))}
        </ClientCarousel>
      </div>

      {/* The carousel is client-only (ssr: false), so keep every link in the
          static HTML for crawlers and no-JS visitors. */}
      <noscript>
        {[...groupByRegion(locations.usa), ...groupByRegion(locations.ca)].map(
          ({ region, items, hideTitle }, idx) => (
            <div
              key={`noscript-region-${idx}`}
              className="mt-6 contentContainer"
            >
              <RegionCard
                region={region}
                locations={items}
                hideTitle={hideTitle}
              />
            </div>
          ),
        )}
      </noscript>
    </>
  );
}
